import {
  Button,
  ButtonGroup,
  closeCurrentDialog,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {z} from 'zod';

import {useCreateTelephonyLineMutation} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

const LINE_NAME_MAX_LENGTH = 255;

const createTelephonyLineSchema = z.object({
  name: z.string().trim().min(1).max(LINE_NAME_MAX_LENGTH),
});

type CreateTelephonyLineFormValues = z.infer<typeof createTelephonyLineSchema>;

export function CreateTelephonyLineForm(props: RequiredTestIdProps) {
  const navigate = useNavigate();

  const [createLine] = useCreateTelephonyLineMutation();

  const handleSubmit: FormSubmitHandler<CreateTelephonyLineFormValues> = async (values) => {
    await createLine({body: {name: values.name}})
      .unwrap()
      .then((createdLine) => {
        closeCurrentDialog();
        navigate(
          composePath(settingsRoutes.telephonyLineDetail, {params: {lineId: createdLine.id}})
        );
      })
      .catch(handleApiError);
  };

  return (
    <Form<CreateTelephonyLineFormValues>
      experimentalZodSchema={createTelephonyLineSchema}
      onSubmit={handleSubmit}
      defaultValues={{name: ''}}
    >
      {(control) => (
        <VStack spacing={4}>
          <FormField
            control={control}
            name="name"
            type="text"
            label={i18n.t('general.labels.name')}
            isRequired
            data-testid={suffixTestId('name', props)}
          />
          <ButtonGroup align="right">
            <Button
              variant="secondary"
              title={i18n.t('general.actions.cancel')}
              onClick={closeCurrentDialog}
              data-testid={suffixTestId('cancel', props)}
            />
            <FormButton
              control={control}
              type="submit"
              variant="primary"
              title={i18n.t('general.actions.create')}
              data-testid={suffixTestId('submit', props)}
            />
          </ButtonGroup>
        </VStack>
      )}
    </Form>
  );
}
